import React, { useImperativeHandle, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import * as yup from 'yup'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'

import DatePickerField from '@/components/formik/fields/DatePickerField'
import PhoneNumberField from '@/components/formik/fields/PhoneNumberField'
import SelectField from '@/components/formik/fields/SelectField'
import TextField from '@/components/formik/fields/TextField'
import Signature from './Signature'
import { StaffsPrivacy } from '@/core/models/staff.model'

export type ProfileFormValues = {
  civility: string
  title: string
  birthday: Date | null
  city: string
  cp: string
  cps: string
  email: string
  gender: 'M' | 'F' | 'I'
  name: string
  phone: string
  speciality: string
  street: string
  surname: string
  signature: string
  baudrate: number
  privacy: StaffsPrivacy
  conflictOfInterest: string
}

export type ProfileFormProps = {
  initialValues: ProfileFormValues
  isSubmitting?: boolean
  onSubmit: (values: ProfileFormValues) => void
}

export type ProfileFormHandle = {
  toggleEditable: () => void
}

const profileFormValidationSchema = yup.object().shape({
  civility: yup.string().default(''),
  title: yup.string().default(''),
  birthday: yup.date().max(new Date()).nullable().typeError('yup:date.invalidDate'),
  city: yup.string().default(''),
  cp: yup.string().default(''),
  cps: yup.string().default(''),
  email: yup.string().email().required(),
  gender: yup.string().default(''),
  name: yup.string().required(),
  phone: yup.string().default(''),
  speciality: yup.string().default(''),
  street: yup.string().default(''),
  surname: yup.string().required(),
  signature: yup.string().default(''),
  baudRate: yup.number().default(9600),
  privacy: yup.string().default(StaffsPrivacy.Establishment),
  conflictOfInterest: yup.string().default('')
})

function ProfileForm(props: ProfileFormProps, ref: React.Ref<ProfileFormHandle>) {
  const { initialValues, isSubmitting, onSubmit } = props
  const { t } = useTranslation()
  const [editable, setEditable] = useState<boolean>(false)

  useImperativeHandle(ref, () => ({
    toggleEditable: () => {
      setEditable(!editable)
    }
  }))

  const genderOptions = useMemo(() => ([
    { label: t('field.gender.options.male.label'), value: 'M' },
    { label: t('field.gender.options.female.label'), value: 'F' },
    { label: t('field.gender.options.unknown.label'), value: 'I' }
  ]), [t])

  const civilityOptions = useMemo(() => ([
    { label: 'Dr.', value: 'DR' },
    { label: 'Pr.', value: 'PR' },
    { label: 'M.', value: 'MR' },
    { label: 'Mme', value: 'MME' }
  ]), [])

  const privacyOptions = useMemo(() => ([
    { label: t('field.privacy.options.establishment.label'), value: StaffsPrivacy.Establishment },
    { label: t('field.privacy.options.public.label'), value: StaffsPrivacy.Public },
    { label: t('field.privacy.options.invisble.label'), value: StaffsPrivacy.Invisble }
  ]), [t])

  const trainingOptions = useMemo(() => ([
    { label: t('enum.TrainingType.INTERNAL'), value: 'INTERNAL' },
    { label: t('enum.TrainingType.DPC'), value: 'DPC' },
    { label: t('enum.TrainingType.LEARNED_SOCIETY'), value: 'LEARNED_SOCIETY' },
    { label: t('enum.TrainingType.OTHER'), value: 'OTHER' },
    { label: t('enum.TrainingType.NONE'), value: 'NONE' }
  ]), [t])

  const handleToggleEditable = () => {
    setEditable(!editable)
  }

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={profileFormValidationSchema}
      >
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Grid container spacing={3}>
              <Grid container item spacing={4}>
                <Grid item xs={12} sm={6} md={2}>
                  <SelectField readOnly={!editable} fullWidth label={t('field.civility.label')} name="civility" options={civilityOptions} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField readOnly={!editable} fullWidth label={t('field.firstName.label')} name="name" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField readOnly={!editable} fullWidth label={t('field.lastName.label')} name="surname" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField readOnly={!editable} fullWidth label={t('field.title.label')} name="title" />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <SelectField readOnly={!editable} fullWidth label={t('field.gender.label')} name="gender" options={genderOptions} />
                </Grid>
                <Grid item xs={12} sm={6} md={8}>
                  <TextField readOnly={!editable} fullWidth label={t('field.email.label')} name="email" />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <PhoneNumberField readOnly={!editable} label={t('field.phone.label')} name="phone" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField readOnly={!editable} fullWidth label={t('field.speciality.label')} name="speciality" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField readOnly={!editable} fullWidth label={t('field.cps.label')} name="cps" />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12} sm={6} md={6}>
                  <DatePickerField disableFuture readOnly={!editable} label={t('field.birthday.label')} name="birthday" />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12}>
                  <TextField readOnly={!editable} fullWidth label={t('field.street.label')} name="street" />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField readOnly={!editable} fullWidth label={t('field.city.label')} name="city" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField readOnly={!editable} fullWidth label={t('field.postalCode.label')} name="cp" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField readOnly={!editable} fullWidth label={t('field.baudRate.label')} name="baudrate" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <SelectField readOnly={!editable} fullWidth label={t('field.privacy.label')} name="privacy" options={privacyOptions} />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12}>
                  <TextField readOnly={!editable} fullWidth multiline rows={3} label={t('field.conflictOfInterest.label')} name="conflictOfInterest" />
                </Grid>
              </Grid>
              <Grid container item spacing={4}>
                <Grid item xs={12}>
                  <Signature readOnly={editable} src={"data:image/png;base64," + initialValues.signature} onEnd={(dataUrl: string) =>
                    formikProps.setFieldValue("signature", dataUrl)} />
                </Grid>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'end', mt: '36px' }}>
              {editable ? (
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="neutral"
                    onClick={() => {
                      formikProps.handleReset()
                      handleToggleEditable()
                    }}
                  >
                    {t('common:button.cancel.label')}
                  </Button>
                  <LoadingButton data-testid="profile-form-submit" color="primary" disableElevation loading={isSubmitting} type="submit" variant="contained">
                    {t('common:button.submit.label')}
                  </LoadingButton>
                </Stack>
              ) : (
                <Button data-testid="profile-form-edit" color="primary" disableElevation variant="contained" onClick={handleToggleEditable}>
                  {t('common:button.modify.label')}
                </Button>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )

}

export default React.forwardRef(ProfileForm)
